import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Table, ToastContainer } from '../ui';
import { safeErrorMessage } from '../../utils/apiError';
import { hasAnyRole } from '../../utils/roles';

const STATUSES = ['present', 'absent', 'late', 'excused'];

const todayISO = () => new Date().toISOString().slice(0, 10);

function HomeroomRegister({ token, user }) {
  const canReopen = hasAnyRole(user, ['administrator', 'principal', 'head_of_academics']);
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState('');
  const [date, setDate] = useState(todayISO());
  const [session, setSession] = useState('AM');
  const [register, setRegister] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = 'success') => setToasts(prev => [...prev, { message, type }]);
  const removeToast = (index) => setToasts(prev => prev.filter((_, i) => i !== index));

  useEffect(() => {
    fetch('/api/v1/classes', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setClasses)
      .catch(() => {});
  }, [token]);

  const fetchRegister = useCallback(async () => {
    if (!classId || !date || !session) return;
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/attendance/homeroom/register?class_id=${classId}&date=${date}&session=${session}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setRegister(data);
        setRecords(data.records);
      } else {
        addToast(await safeErrorMessage(response, 'Failed to load register'), 'error');
      }
    } catch {
      addToast('Network error. Please check your connection and try again.', 'error');
    } finally {
      setLoading(false);
    }
  }, [token, classId, date, session]);

  useEffect(() => { fetchRegister(); }, [fetchRegister]);

  const setStudentStatus = (studentId, status) => {
    setRecords(prev => prev.map(r => r.student_id === studentId ? { ...r, status } : r));
  };

  const setStudentRemarks = (studentId, remarks) => {
    setRecords(prev => prev.map(r => r.student_id === studentId ? { ...r, remarks } : r));
  };

  const save = async (submit) => {
    setSaving(true);
    try {
      const response = await fetch('/api/v1/attendance/homeroom/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          class_id: classId, date, session,
          records: records.filter(r => r.status).map(r => ({ student_id: r.student_id, status: r.status, remarks: r.remarks })),
          submit,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setRegister(data);
        setRecords(data.records);
        addToast(submit ? 'Register submitted and locked' : 'Draft saved');
      } else {
        addToast(await safeErrorMessage(response, 'Failed to save register'), 'error');
      }
    } catch {
      addToast('Network error. Please check your connection and try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const reopen = async () => {
    if (!register?.id || !window.confirm('Reopen this register for editing?')) return;
    try {
      const response = await fetch(`/api/v1/attendance/registers/${register.id}/reopen`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setRegister(data);
        setRecords(data.records);
      } else {
        addToast(await safeErrorMessage(response, 'Failed to reopen register'), 'error');
      }
    } catch {
      addToast('Network error. Please check your connection and try again.', 'error');
    }
  };

  const locked = register?.status === 'submitted';

  const columns = [
    { header: 'Student', render: (r) => <strong>{r.full_name}</strong> },
    ...STATUSES.map(status => ({
      header: status.charAt(0).toUpperCase() + status.slice(1),
      width: '90px',
      render: (r) => (
        <input
          type="radio"
          name={`homeroom-status-${r.student_id}`}
          checked={r.status === status}
          disabled={locked}
          onChange={() => setStudentStatus(r.student_id, status)}
        />
      ),
    })),
    {
      header: 'Remarks',
      render: (r) => (
        <input
          type="text" placeholder="Remarks" value={r.remarks || ''} disabled={locked}
          onChange={(e) => setStudentRemarks(r.student_id, e.target.value)}
        />
      ),
    },
  ];

  return (
    <div>
      <Card title="Homeroom Attendance">
        <div className="form-row">
          <div className="form-group">
            <label>Class *</label>
            <select value={classId} onChange={(e) => setClassId(e.target.value)}>
              <option value="">Select Class</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.display_name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Date *</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Session *</label>
            <select value={session} onChange={(e) => setSession(e.target.value)}>
              <option value="AM">Morning</option>
              <option value="PM">Afternoon</option>
            </select>
          </div>
        </div>

        {locked && (
          <div className="alert alert-success" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Submitted and locked{register.marked_by_name ? ` by ${register.marked_by_name}` : ''}.</span>
            {canReopen && <Button variant="secondary" size="sm" onClick={reopen}>Reopen</Button>}
          </div>
        )}

        {loading ? (
          <p>Loading…</p>
        ) : !classId ? (
          <p style={{ color: 'var(--color-text-muted)' }}>Select a class to take attendance.</p>
        ) : (
          <div style={{ marginTop: '1rem' }}>
            <Table columns={columns} data={records} emptyMessage="No active students enrolled in this class." />
          </div>
        )}

        {!locked && classId && records.length > 0 && (
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <Button variant="secondary" onClick={() => save(false)} disabled={saving}>Save Draft</Button>
            <Button variant="primary" onClick={() => save(true)} disabled={saving}>Submit & Lock</Button>
          </div>
        )}
      </Card>

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
}

export default HomeroomRegister;
